import { Box, Typography } from '@mui/material';
import { useState } from 'react';

import { Button, ModalContentContainer, TextField } from '@/components';
import { urls } from '@/constants';
import { Link } from '@/containers';
import { useForm } from '@/hooks';
import { AnalyticsService, EVENTS } from '@/services';
import { useAddRecoveryEmailMutation } from '@/store';

type AddRecoveryEmailFormState = {
  email: string;
};

const STEPS = {
  INITIAL: 0,
  SUCCESS: 1,
} as const;

export const AddRecoveryEmail = ({
  onSuccess,
  onCancel,
}: {
  onSuccess?: () => void;
  onCancel?: () => void;
}) => {
  const [addRecoveryEmail] = useAddRecoveryEmailMutation();
  const { form, errors, setErrors, onChange, validateForm, resetForm } =
    useForm<AddRecoveryEmailFormState>({
      email: '',
    });
  const [step, setStep] = useState<(typeof STEPS)[keyof typeof STEPS]>(0);

  const onSubmit = async () => {
    const isValid = validateForm();
    if (!isValid) {
      return;
    }
    try {
      AnalyticsService.logEvent(
        EVENTS.ACCOUNT_RECOVERY_EVENTS.ADD_RECOVERY_EMAIL.INTENT,
        { recoveryEmail: form.email.trim() },
      );
      await addRecoveryEmail({
        recoveryEmail: form.email.trim(),
      }).unwrap();
      setStep(STEPS.SUCCESS);
      AnalyticsService.logEvent(
        EVENTS.ACCOUNT_RECOVERY_EVENTS.ADD_RECOVERY_EMAIL.SUCCESS,
      );
    } catch (error) {
      const { data } = error as { data: { message: string } };
      if (data?.message && typeof data?.message === 'string') {
        setErrors(prev => ({
          ...prev,
          email: data?.message,
        }));
      } else {
        setErrors(prev => ({
          ...prev,
          email: 'Something went wrong. Please try again or contact support.',
        }));
      }
      AnalyticsService.logEvent(
        EVENTS.ACCOUNT_RECOVERY_EVENTS.ADD_RECOVERY_EMAIL.FAIL,
        {
          error: data?.message || '',
        },
      );
    }
  };

  return (
    <ModalContentContainer>
      {step === STEPS.INITIAL && (
        <>
          <Typography variant="h1">Add Recovery Email</Typography>
          <TextField
            id="email"
            fullWidth
            label="Email"
            placeholder="Enter your email"
            type="email"
            sx={{ mt: 6 }}
            value={form.email}
            error={!!errors.email}
            helperText={errors.email}
            onChange={onChange}
          />
          <Typography variant="body1" sx={{ mt: 2 }}>
            <p>
              Stay connected to your account and certificates, even if you lose
              access to your login email.
            </p>
            <p>
              Learn more about recovery emails{' '}
              <Link to={urls.intercom.addRecoveryEmailArticle} target="_blank">
                here
              </Link>
              .
            </p>
          </Typography>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              gap: 2,
              mt: 4,
            }}>
            <Button
              variant="secondary"
              label="Cancel"
              skinny={false}
              fullWidth={false}
              sx={{ width: '50%' }}
              onClick={() => {
                onCancel?.();
                resetForm();
              }}
            />
            <Button
              variant="primary"
              label="Save"
              fullWidth={false}
              skinny={false}
              sx={{ width: '50%' }}
              onClick={onSubmit}
            />
          </Box>
        </>
      )}
      {step === STEPS.SUCCESS && (
        <>
          <Typography variant="h1">Check Your Email</Typography>
          <Typography variant="body1" sx={{ mt: 2 }}>
            We&apos;ve sent a confirmation email to:
            <br />
            <strong>{form.email}</strong>
            <br />
            <br />
            Click the link inside to verify your recovery email address.
            <br />
            <br />
            If you don&apos;t see the email you can resend it or choose a
            different email address within the Profile section.
          </Typography>
          <Button
            variant="primary"
            label="Got it"
            fullWidth
            skinny={false}
            sx={{ mt: 2, height: 48, fontSize: 16 }}
            onClick={() => {
              onSuccess?.();
              resetForm();
            }}
          />
        </>
      )}
    </ModalContentContainer>
  );
};
